import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { api } from "../../lib/api";
import { AlertTriangle, Package, TrendingUp, ShoppingCart, Wine } from "lucide-react";

const COLORS = ["#7A2A36", "#D4A93C", "#4F7942", "#A6534F", "#6B4226", "#8B7355", "#C28840"];

export default function AdminMarketplaceAnalytics() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/analytics/marketplace").then((r) => setData(r.data)); }, []);

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    return (
        <section className="space-y-6" data-testid="admin-analytics">
            <div>
                <span className="label-overline text-wine/60">Marketplace</span>
                <h1 className="font-heading text-3xl text-wine-dark">Analytics</h1>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Kpi label="Produtos" value={data.kpis.products_total} sub={`${data.kpis.products_approved} aprovados`} icon={Package} />
                <Kpi label="Encomendas" value={data.kpis.orders_total} icon={ShoppingCart} />
                <Kpi label="Pagas" value={data.kpis.orders_paid} icon={ShoppingCart} />
                <Kpi label="Conversão pagamento" value={`${data.kpis.conversion_pct}%`} icon={TrendingUp} accent />
                <Kpi label="Clube" value={data.subscriptions_split.reduce((s, p) => s + p.count, 0)} icon={Wine} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Mix por categoria (receita)">
                    {data.by_category.length === 0 ? <Empty /> : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.by_category}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#7A2A36" strokeOpacity={0.08} vertical={false} />
                                    <XAxis dataKey="name" stroke="#7A2A36" strokeOpacity={0.7} fontSize={11} />
                                    <YAxis stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} tickFormatter={(v) => `€${v}`} />
                                    <Tooltip contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }} formatter={(v, n) => n === "revenue" ? [`€${v}`, "Receita"] : [v, "Unidades"]} />
                                    <Bar dataKey="revenue" fill="#7A2A36" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>

                <Card title="Distribuição do Clube">
                    {data.subscriptions_split.length === 0 ? <Empty /> : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={data.subscriptions_split} dataKey="count" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2} label={(e) => `${e.name}: ${e.count}`} labelLine={false}>
                                        {data.subscriptions_split.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Top produtos vendidos">
                    {data.top_sold.length === 0 ? <Empty /> : (
                        <div className="space-y-2">
                            {data.top_sold.map((p, i) => (
                                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-wine-dark/5 last:border-0" data-testid={`top-sold-${i}`}>
                                    <span className="font-heading text-2xl text-wine-dark/25 w-8">{String(i + 1).padStart(2, "0")}</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-wine-dark font-semibold truncate">{p.name}</p>
                                        <p className="text-xs text-wine-dark/55">{p.category} · stock: {p.stock}</p>
                                    </div>
                                    <span className="label-overline text-vineyard">{p.sales_count} un.</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                <Card title="Regiões">
                    {data.by_region.length === 0 ? <Empty /> : (
                        <div className="space-y-2">
                            {data.by_region.map((r) => (
                                <div key={r.name} className="flex items-center justify-between py-2 border-b border-wine-dark/5 last:border-0 text-sm">
                                    <span className="text-wine-dark">{r.name}</span>
                                    <span className="text-xs text-wine-dark/60">{r.products} produtos · {r.units_sold} vendidos</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Sem stock" warn={data.out_of_stock.length > 0}>
                    {data.out_of_stock.length === 0 ? <p className="text-vineyard text-sm">Nenhum produto sem stock.</p> : (
                        <ul className="space-y-1.5">
                            {data.out_of_stock.map((p) => (
                                <li key={p.id} className="flex items-center gap-2 text-sm text-wine-dark">
                                    <AlertTriangle className="h-3.5 w-3.5 text-destructive" /> {p.name}
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                <Card title="Stock baixo (≤3)" warn={data.low_stock.length > 0}>
                    {data.low_stock.length === 0 ? <p className="text-vineyard text-sm">Tudo abastecido.</p> : (
                        <ul className="space-y-1.5">
                            {data.low_stock.map((p) => (
                                <li key={p.id} className="flex items-center gap-2 text-sm text-wine-dark">
                                    <AlertTriangle className="h-3.5 w-3.5 text-gold-deep" /> {p.name} <span className="text-xs text-wine-dark/55">· {p.stock} un.</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </section>
    );
}

function Kpi({ label, value, sub, icon: Icon, accent }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${accent ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${accent ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${accent ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2">{value}</p>
            {sub && <p className={`text-xs mt-1 ${accent ? "text-cream/60" : "text-wine-dark/55"}`}>{sub}</p>}
        </div>
    );
}

function Card({ title, children, warn }) {
    return (
        <div className={`bg-white rounded-md border p-5 shadow-sm ${warn ? "border-gold-soft/60" : "border-gold-soft/30"}`}>
            <h2 className="font-heading text-xl text-wine-dark mb-4">{title}</h2>
            {children}
        </div>
    );
}

function Empty() { return <p className="text-wine-dark/55 text-sm">Sem dados.</p>; }
